import Translations from '@helpers/translations'
import {
  useForm,
  FormProvider,
  SubmitHandler,
  useFormState
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { InputField } from '../../../ui/formElements/form/InputField'
import styles from './styles.module.scss'

type EmailFormProps = {
  onSubmit: (email: string) => void
  children: (name: string, isValid: boolean) => React.ReactNode
  classes?: string | undefined
  placeholder?: string
  honeypot?: boolean
  onBotDetected?: () => void
}

const emailSchema = z.object({
  newsletter: InputField.getSchema('email', true),
  nl_hp: z.string().optional()
})

type EmailFormData = z.infer<typeof emailSchema>

export function EmailForm({
  onSubmit,
  classes,
  placeholder,
  honeypot,
  onBotDetected,
  children
}: EmailFormProps) {
  const methods = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { newsletter: '', nl_hp: '' }
  })

  const { handleSubmit, watch, control, register } = methods
  const { isValid } = useFormState({ control })
  const email = watch('newsletter')

  const submitHandler: SubmitHandler<EmailFormData> = data => {
    if (honeypot && data.nl_hp) {
      onBotDetected?.()
      return
    }

    onSubmit(data.newsletter)
  }

  return (
    <FormProvider {...methods}>
      <form
        method="post"
        className={classes}
        onSubmit={handleSubmit(submitHandler)}
        noValidate
      >
        <InputField<EmailFormData>
          placeholder={
            placeholder ||
            Translations.localise(
              'common.newsletter-modal.form.email-placeholder'
            )
          }
          name="newsletter"
          type="email"
          required
          label="Email"
        />
        {honeypot && (
          <input
            {...register('nl_hp')}
            type="text"
            className={styles.honeypot}
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
          />
        )}
        {children(email, isValid)}
      </form>
    </FormProvider>
  )
}
